import { useFormatters } from '@/hooks/useFormatters';
import { OC_DEFINE_ROW_AGO_SX } from '@/theme/au-compat';
import { GateDataRenderer } from '@features/gateCommon';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { Trans } from '@lingui/macro';
import { Skeleton, Typography } from '@mui/material';
import { type FC } from 'react';
import type { GateLiveStatusState } from '../hooks/useGateLiveStatus';
import { AwaitingDevice } from './AwaitingDevice';
import { DisplayBox } from './DisplayBox';

interface Props {
  gateId: string | null;
  live: GateLiveStatusState;
}

/** La posición del equipo si respondió; si no, la última registrada, con su antigüedad. */
export const LastPosition: FC<Props> = ({ gateId, live }) => {
  const { formatMetricValue, formatRelativeTime } = useFormatters();
  const livePosition = live.reading?.position ?? null;

  let value;
  if (!gateId) value = <Trans>Dispositivo no seleccionado</Trans>;
  else if (!live.awaiting && livePosition !== null)
    value = (
      <>
        {formatPosition(livePosition, formatMetricValue)}{' '}
        <Typography component="span" sx={OC_DEFINE_ROW_AGO_SX}>
          / <Trans>en vivo</Trans>
        </Typography>
      </>
    );
  // Montado también mientras se espera: la consulta a la base corre en paralelo con la del equipo.
  else
    value = (
      <GateDataRenderer
        gateId={gateId}
        queryOptions={{ fetchPolicy: 'no-cache' }}
        render={({ lastPosition }) => {
          if (live.awaiting) return <AwaitingDevice />;
          if (!lastPosition) return <Trans>Sin datos</Trans>;
          const [timestamp, position] = lastPosition;
          return (
            <>
              {formatPosition(position, formatMetricValue)}{' '}
              <Typography component="span" sx={OC_DEFINE_ROW_AGO_SX}>
                / <Trans>registrada {formatRelativeTime(timestamp)}</Trans>
              </Typography>
            </>
          );
        }}
        fallback={() => (live.awaiting ? <AwaitingDevice /> : <Skeleton>Estado dummy</Skeleton>)}
      />
    );

  return <DisplayBox label={<Trans>Posición actual</Trans>}>{value}</DisplayBox>;
};
